import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Question } from '../../../../types/api';
import { SelectDropdown } from '../../SelectDropdown';
import { apiUtils } from '../../../../hooks/useApi';
import { useDebounce } from '../../../../hooks/useDebounce';

interface SearchableInputProps {
  question: Question;
  value: any;
  onChange: (value: any) => void;
  countryId?: number | null;
  destinationId?: number | null;
  hasCountryChanged?: boolean;
  hasDestinationChanged?: boolean;
}

interface SelectOption {
  label: string;
  value: string;
}

export const SearchableInput: React.FC<SearchableInputProps> = ({
  question,
  value,
  onChange,
  countryId,
  destinationId,
  hasCountryChanged,
  hasDestinationChanged,
}) => {
  console.log("value ::", value)
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Get config from question
  const searchEndpoint = question.config?.searchEndpoint;
  const valueField = question.config?.valueField || 'id';
  const labelField = question.config?.labelField || 'name';

  // Search function
  const searchLocations = useCallback(
    async (term: string) => {
      if (!searchEndpoint || !term) {
        setOptions([]);
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      setIsLoading(true);

      try {
        // Build query params
        const params: Record<string, any> = { name: term };

        if (countryId) params.country_id = countryId;
        if (question.id === 'giata_city_id' && destinationId) {
          params.destination_id = destinationId;
        }

        // Make API call
        const response = await apiUtils.get(searchEndpoint, {
          ...params,
          signal: abortControllerRef.current.signal,
        });

        const data = response.data?.data || response.data || [];

        // Transform to options
        const transformedOptions: SelectOption[] = data.map((item: any) => ({
          label: item[labelField],
          value: String(item[valueField]),
        }));

        setOptions(transformedOptions);
      } catch (error: any) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Search error:', error);
        }
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      searchEndpoint,
      labelField,
      valueField,
      countryId,
      destinationId,
      question.id,
    ]
  );

  // Trigger search when debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm) {
      searchLocations(debouncedSearchTerm);
    } else {
      setOptions([]);
    }
  }, [debouncedSearchTerm, searchLocations]);

  // Determine if this field should be cleared based on parent field changes
  const shouldClearField = useMemo(() => {
    // Destination field: clear only when country changes
    if (question.id === 'giata_destination_id') {
      return hasCountryChanged;
    }
    // City field: clear when country changes OR destination changes
    if (question.id === 'giata_city_id') {
      return hasCountryChanged || hasDestinationChanged;
    }
    // Default: only country change affects it
    return hasCountryChanged;
  }, [question.id, hasCountryChanged, hasDestinationChanged]);

  // Clear options when dependencies change or when parent fields change
  useEffect(() => {
    setOptions([]);
    setSearchTerm('');
  }, [
    countryId,
    destinationId,
    hasCountryChanged,
    hasDestinationChanged,
    question.id,
  ]);

  // Handle selection
  const handleChange = (selectedValue: string) => {
    setSearchTerm('');
    const selectedOption = options.find(opt => opt.value === selectedValue);

    if (selectedOption) {
      onChange({
        selectedOptionId: selectedValue,
        typedValue: null,
        label: selectedOption.label,
      });
    } else {
      onChange({
        selectedOptionId: null,
        typedValue: null,
        label: undefined,
      });
    }
  };

  const currentValue = useMemo(() => {
    if (typeof value === 'string') { 
      return value;
    }
    if (shouldClearField || !value?.selectedOptionId) {
      return '';
    }
    return value?.selectedOptionId || '';
  }, [shouldClearField, value, question.id]);

  // Memoize placeholder - recalculates when parent fields change
  const placeholder = useMemo(() => {
    if (shouldClearField) {
      // For city field, show more specific message if destination changed but not country
      if (question.id === "giata_city_id") {
        if ((!countryId)) {
          return 'Select country first';
        }
        if (!destinationId) {
          return 'Please Select destination first';
        }
        else if (hasCountryChanged && destinationId) {
          return 'Please Select destination first';
        }
      }
      else if (question.id === "giata_destination_id") { 
        if (!countryId) {
          return 'Select country first';
        }
      }
      return 'Search...';
    }

    if (question.id === "giata_city_id") {
      if(!countryId) {
        return 'Select country first';
      }
      if(!destinationId) {
        return 'Select destination first';
      }
    }

    if(question.id === "giata_destination_id" && !countryId) {
      return 'Select country first';
    }
    return value?.label || question.placeholder || 'Search...';
  }, [
    shouldClearField,
    value?.label,
    question.placeholder,
    question.id,
    countryId,
    destinationId,
    hasCountryChanged,
  ]);

  const isDisabled =
    question.attributes?.readOnly ||
    (question.id === 'giata_destination_id' && !countryId) ||
    (question.id === 'giata_city_id' && (!countryId || !destinationId));

  return (
    <div className="flex flex-col gap-2">
      <SelectDropdown
        options={options}
        value={currentValue}
        onChange={handleChange}
        searchable
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        isSearchLoading={isLoading}
        placeholder={placeholder}
        disabled={isDisabled}
        className="!max-h-12"
        variant="transparent"
      />
      {searchTerm && !isLoading && options.length === 0 && (
        <p className="text-sm text-gray-500">
          No results found for "{searchTerm}"
        </p>
      )}
    </div>
  );
};
